import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { AuthProvider } from '@/lib/auth'
import { initSentry } from '@/lib/sentry'
import '@/i18n'
import '@/styles/globals.css'
import { routeTree } from './routeTree.gen'

initSentry()

// Apply the persisted theme before React mounts so the user doesn't
// see a light flash if they've chosen dark (or vice versa). The
// useTheme hook reconciles on mount for any subsequent changes.
function applyInitialTheme() {
  try {
    const stored = localStorage.getItem('crawfish.budget.theme')
    const mode = stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system'
    const dark =
      mode === 'dark' ||
      (mode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
    document.documentElement.classList.toggle('dark', dark)
  } catch {
    // localStorage failures (private mode) → leave whatever the
    // browser default would be.
  }
}
applyInitialTheme()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Sensible defaults — TanStack Query's default is "always
      // stale" which causes a refetch on every component mount. For
      // budgeting data, 60s is fine; the active month subscribes via
      // Firestore listener anyway.
      staleTime: 60_000,
      retry: 1,
    },
  },
})

const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Missing #root in index.html')

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
)
